import { cn } from '@/shared/utils/cn';
import { Input } from '@/shared/widgets/input';
import { FloatingLabel, FormFieldShell } from './FormFieldShell';
import { getFloatingFieldWrapperClass, useFloatingLabelState } from './floatingLabel.utils';
import {
  getFloatingInputClass,
  getStackedInputClass,
} from './formField.styles';
import type { FormTextFieldProps } from './formField.types';
import { useFormFieldVariant } from './useFormFieldVariant';

export function FormTextField({
  label,
  error,
  required = true,
  variant,
  className,
  endAdornment,
  footer,
  disabled = false,
  value,
  type = 'text',
  onFocus,
  onBlur,
  ...props
}: FormTextFieldProps) {
  const resolvedVariant = useFormFieldVariant(variant);
  const hasError = Boolean(error);
  const stringValue = value == null ? '' : String(value);
  const isFilled = stringValue.length > 0;
  const hasEndAdornment = Boolean(endAdornment);
  const { isLabelActive, isFocused, mergeFocusHandlers } = useFloatingLabelState(isFilled);

  if (resolvedVariant === 'floating') {
    return (
      <FormFieldShell
        variant="floating"
        label={label}
        error={error}
        required={required}
        className={className}
      >
        <div
          className={getFloatingFieldWrapperClass(isFilled, disabled, hasError, isFocused)}
        >
          <FloatingLabel label={label} isActive={isLabelActive} />
          <input
            type={type}
            value={value}
            disabled={disabled}
            className={getFloatingInputClass(hasEndAdornment, Boolean(footer), disabled)}
            aria-invalid={hasError}
            aria-label={label}
            {...mergeFocusHandlers(onFocus, onBlur)}
            {...props}
          />
          {footer}
          {endAdornment && (
            <div className="absolute right-1 top-1 bottom-1 z-10 flex items-stretch">{endAdornment}</div>
          )}
        </div>
      </FormFieldShell>
    );
  }

  return (
    <FormFieldShell variant="stacked" label={label} error={error} required={required} className={className}>
      <div className={cn('flex items-start gap-2', hasEndAdornment && 'w-full')}>
        <Input
          type={type}
          value={value}
          disabled={disabled}
          className={cn(getStackedInputClass(hasError), hasEndAdornment && 'flex-1')}
          aria-invalid={hasError}
          onFocus={onFocus}
          onBlur={onBlur}
          {...props}
        />
        {endAdornment}
      </div>
      {footer}
    </FormFieldShell>
  );
}
